import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ConnectPayment, ConnectStatus, RecentPayments, RefundReason, disconnectPayments, getPaymentsStatus,
  getRecentPayments, refundPayment, startPaymentsConnect,
} from '../../api/admin';
import { money } from '../platform/planDisplay';
import { FigureTile, Modal, RowCard, TableCard } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { PHONE, useMediaQuery } from '../../shared/media';

/**
 * Settings → Payments (R11, screen 35): the account's OWN Stripe account,
 * collecting money on its forms. This is the customer's money, not ours —
 * distinct from Settings → Billing & plan (them paying us), and the page
 * says so.
 *
 * Connect status and Disconnect, the "onboarding not finished" and "not
 * configured" states as before; new are the month's figures, the recent
 * payments read live from the connected account, and a Refund on each —
 * full or partial, capped at what Stripe says is still refundable, with a
 * reason, made ON the connected account and written to the audit log.
 */

const PAYOUT_WORD: Record<string, string> = { daily: 'daily', weekly: 'weekly', monthly: 'monthly', manual: 'manual payouts' };

/** "Today 2:47 PM", "Yesterday 9:10 AM", "Sep 22 12:12 PM" — the Responses table's words. */
function whenWords(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const day = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (day(d) === day(now)) return `Today ${time}`;
  if (day(d) === day(yesterday)) return `Yesterday ${time}`;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
  return `${date} ${time}`;
}

/** Sums per currency, the largest first: "$1,243 + EUR 120". */
const sums = (list: { currency: string; cents: number }[]): string =>
  list.map((x) => money(x.cents, x.currency)).join(' + ');

const who = (p: ConnectPayment): string => p.respondentName || p.respondentEmail || 'Someone';

const STATUS_PILL: Record<ConnectPayment['status'], { label: string; tone: string }> = {
  paid: { label: 'Paid', tone: 'ok' },
  partially_refunded: { label: 'Partly refunded', tone: 'warn' },
  refunded: { label: 'Refunded', tone: 'neutral' },
};

export default function PaymentsSettings() {
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [warning, setWarning] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [recent, setRecent] = useState<RecentPayments | null>(null);
  const [refundFor, setRefundFor] = useState<ConnectPayment | null>(null);
  const toast = useToast();
  const phone = useMediaQuery(PHONE);

  const load = useCallback(() => {
    getPaymentsStatus()
      .then((r) => {
        setStatus(r.status); setWarning(r.warning || '');
        // A failed read is a state the table says, never a "Loading…" that never ends.
        if (r.status.configured) {
          getRecentPayments().then(setRecent).catch(() => setRecent({
            available: false, payments: [], month: null, formsTakingPayment: [],
            warning: 'Your payments could not be read right now. Try again in a minute.',
          }));
        }
      })
      .catch((e) => setError(e.message));
  }, []);

  // Re-check on focus too: onboarding happens in a Stripe tab, and coming
  // back here is exactly when the answer changes.
  useEffect(() => {
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);

  async function onConnect() {
    setBusy(true); setError('');
    try {
      const r = await startPaymentsConnect();
      window.location.href = r.url;
    } catch (e: any) {
      setError(e.message || 'Could not start Stripe onboarding.');
      setBusy(false);
    }
  }

  async function onDisconnect() {
    setBusy(true); setError('');
    try {
      await disconnectPayments();
      setDisconnecting(false);
      toast('Stripe disconnected. Forms with a Payment field stop taking payments until you reconnect.');
      load();
    } catch (e: any) { setError(e.message); setDisconnecting(false); }
    finally { setBusy(false); }
  }

  function onRefunded(p: ConnectPayment, refundedCents: number) {
    setRefundFor(null);
    setRecent((cur) => cur && { ...cur, payments: cur.payments.map((x) => (x.id === p.id ? p : x)) });
    toast(`Refunded ${money(refundedCents, p.currency)} to ${who(p)}.`);
  }

  if (!status) {
    return <div className="fs-card pad st-col">{error ? <div className="ad-err">{error}</div> : 'Loading…'}</div>;
  }

  const live = status.connected && status.chargesEnabled;
  const details = status.details;
  const forms = recent?.formsTakingPayment ?? [];
  const month = recent?.month ?? null;
  const formsCaption = forms.length === 0
    ? 'add a Payment field in the builder'
    : forms.length <= 2 ? forms.map((f) => f.name).join(', ') : `${forms[0].name} and ${forms.length - 1} more`;

  return (
    <div className="st-stack bp-pay">
      <div className="fs-page-head">
        <div className="text">
          <h1>Payments</h1>
          <div className="fs-page-sub">
            Customer payments go straight to your own Stripe account — payouts, receipts and disputes
            live in your Stripe dashboard. Your Form-Magic subscription is separate, under Billing &amp; plan.
          </div>
        </div>
        {status.connected && (
          <a className="fs-btn primary" href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer">
            Open Stripe ↗
          </a>
        )}
      </div>

      {error && <div className="ad-err">{error}</div>}
      {warning && <div className="ad-err">{warning}</div>}
      {recent?.warning && <div className="bl-banner bl-amber" role="status"><span className="bl-banner-text">{recent.warning}</span></div>}

      {live && (
        <div className="fs-tiles bp-tiles">
          <FigureTile
            label="Collected this month"
            value={month && month.collected.length ? sums(month.collected) : money(0, details?.defaultCurrency || 'usd')}
            caption={month
              ? `${month.count}${month.truncated ? '+' : ''} payment${month.count === 1 ? '' : 's'}${month.refunded.length ? ` · ${sums(month.refunded)} refunded` : ''}`
              : 'not available right now'}
          />
          <FigureTile label="Forms taking payment" value={String(forms.length)} caption={formsCaption} />
          <FigureTile
            label="Payouts"
            value={details ? (details.payoutsEnabled ? 'On' : 'Off') : '—'}
            caption={details
              ? [details.payoutInterval ? PAYOUT_WORD[details.payoutInterval] || details.payoutInterval : null,
                details.defaultCurrency ? details.defaultCurrency.toUpperCase() : null].filter(Boolean).join(' · ') || 'set in Stripe'
              : undefined}
            tone={details?.payoutsEnabled ? 'ok' : 'warn'}
          />
        </div>
      )}

      <section className="fs-card st-card" aria-labelledby="bp-connect-title">
        <div className="st-card-head"><h2 id="bp-connect-title">Stripe Connect</h2></div>
        {!status.configured ? (
          <RowCard label="Status">
            <span className="st-muted">
              Stripe is not configured on this server yet, so payments cannot be connected. Ask your platform operator.
            </span>
          </RowCard>
        ) : !status.connected ? (
          <RowCard
            label="Status"
            actions={(
              <button className="fs-btn primary" disabled={busy} onClick={onConnect}>
                {busy ? 'Opening Stripe…' : 'Connect your Stripe account'}
              </button>
            )}
          >
            <span className="st-state off">Not connected</span>
            <div className="st-muted st-note">
              Collect money on your forms with a <b>Payment</b> field. Stripe walks you through setting up
              your account and brings you back here.
            </div>
          </RowCard>
        ) : (
          <>
            <RowCard label="Account" hint={details?.displayName ? <code className="st-mono">{status.accountId}</code> : undefined}>
              {details?.displayName ? <b>{details.displayName}</b> : <code className="st-mono">{status.accountId}</code>}
            </RowCard>
            <RowCard
              label="Status"
              actions={(
                <>
                  {!status.chargesEnabled && (
                    <button className="fs-btn primary sm" disabled={busy} onClick={onConnect}>
                      {busy ? 'Opening Stripe…' : 'Continue Stripe onboarding'}
                    </button>
                  )}
                  <button className="fs-btn text danger" disabled={busy} onClick={() => setDisconnecting(true)}>Disconnect</button>
                </>
              )}
            >
              {status.chargesEnabled ? (
                <>
                  <span className="st-state ok">
                    Connected{details ? ` · payouts ${details.payoutsEnabled ? 'on' : 'off'}` : ''}
                  </span>
                  <div className="st-muted st-note">Ready to take payments. Add a <b>Payment</b> field to any form from the builder&apos;s palette.</div>
                </>
              ) : (
                <>
                  <span className="fs-pill warn">Connected — onboarding not finished</span>
                  <div className="st-muted st-note">
                    Stripe still needs some details before this account can take charges. Continue where you left off.
                  </div>
                </>
              )}
            </RowCard>
          </>
        )}
      </section>

      {live && (
        <TableCard
          title="Recent payments"
          hint="Payments taken on your forms, read from your Stripe account. A refund goes back to the card it came from."
        >
          {!recent ? (
            <div className="st-card-body st-muted">Loading…</div>
          ) : !recent.available ? (
            <div className="st-card-body st-muted">Payments can&apos;t be listed right now.</div>
          ) : recent.payments.length === 0 ? (
            <div className="st-card-body st-muted">No payments yet. When someone pays on a form, it shows here.</div>
          ) : phone ? (
            <div className="bp-pay-list">
              {recent.payments.map((p) => (
                <div className="bp-pay-row" key={p.id}>
                  <div className="head">
                    <span className="who">{who(p)}</span>
                    <span className="amount">{money(p.amountCents, p.currency)}</span>
                  </div>
                  <div className="meta">
                    {p.formName || 'A form'}{p.submissionId ? ` · ${p.submissionId}` : ''} · {whenWords(p.createdAt)}
                  </div>
                  <div className="foot">
                    <span className={`fs-pill ${STATUS_PILL[p.status].tone}`}>{STATUS_PILL[p.status].label}</span>
                    {p.refundableCents > 0 && (
                      <button className="fs-btn sm danger" onClick={() => setRefundFor(p)}>Refund</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className="fs-table">
              <thead>
                <tr>
                  <th>Respondent</th><th>Form</th><th className="num">Amount</th><th>When</th><th>Status</th>
                  <th className="fs-row-actions"><span className="fs-sr-only">Refund</span></th>
                </tr>
              </thead>
              <tbody>
                {recent.payments.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <b className="bp-who">{who(p)}</b>
                      {p.respondentName && p.respondentEmail && <div className="st-muted bp-sub">{p.respondentEmail}</div>}
                    </td>
                    <td>
                      {p.formName || <span className="st-muted">A form no longer here</span>}
                      {p.submissionId && <div className="st-mono bp-sub">{p.submissionId}</div>}
                    </td>
                    <td className="num">
                      {money(p.amountCents, p.currency)}
                      {p.refundedCents > 0 && p.status !== 'refunded' && (
                        <div className="st-muted bp-sub">{money(p.refundedCents, p.currency)} refunded</div>
                      )}
                    </td>
                    <td className="st-muted" style={{ whiteSpace: 'nowrap' }} title={new Date(p.createdAt).toLocaleString()}>
                      {whenWords(p.createdAt)}
                    </td>
                    <td><span className={`fs-pill ${STATUS_PILL[p.status].tone}`}>{STATUS_PILL[p.status].label}</span></td>
                    <td className="fs-row-actions">
                      {p.refundableCents > 0 && (
                        <button className="fs-btn text danger" onClick={() => setRefundFor(p)}
                          aria-label={`Refund ${who(p)}, ${money(p.amountCents, p.currency)}`}>
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>
      )}

      {refundFor && (
        <RefundModal payment={refundFor} onClose={() => setRefundFor(null)} onDone={onRefunded} />
      )}

      {disconnecting && (
        <Modal
          title="Disconnect Stripe?"
          narrow
          onClose={() => setDisconnecting(false)}
          footer={(
            <>
              <button className="fs-btn danger solid" disabled={busy} onClick={() => void onDisconnect()}>
                {busy ? 'Disconnecting…' : 'Disconnect'}
              </button>
              <button className="fs-btn neutral" disabled={busy} onClick={() => setDisconnecting(false)}>Cancel</button>
            </>
          )}
        >
          <p className="bp-modal-text">
            Forms with a Payment field stop taking payments until you reconnect.
          </p>
          <p className="bp-modal-text st-muted">
            Disconnecting only removes the link here — your Stripe account and its money are untouched.
          </p>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The Refund modal.
// ---------------------------------------------------------------------------

const REASONS: { value: '' | RefundReason; label: string }[] = [
  { value: 'requested_by_customer', label: 'Requested by the customer' },
  { value: 'duplicate', label: 'Duplicate payment' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: '', label: 'Other' },
];

/** "12.5" → 1250; null for anything that is not a positive amount with at most two decimals. */
export function parseAmountCents(raw: string): number | null {
  const t = raw.trim().replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const cents = Math.round(parseFloat(t) * 100);
  return cents > 0 ? cents : null;
}

const newKey = (): string => {
  try { return crypto.randomUUID().replace(/-/g, ''); } catch { /* older browsers */ }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Full or partial, capped at the refundable amount, with a reason and a note.
 * The modal is the confirm: it says what will happen in one sentence above the
 * danger button, and one key per opening makes a double-click one refund.
 */
function RefundModal({ payment, onClose, onDone }: {
  payment: ConnectPayment;
  onClose: () => void;
  onDone: (updated: ConnectPayment, refundedCents: number) => void;
}) {
  const [mode, setMode] = useState<'full' | 'partial'>('full');
  const [amount, setAmount] = useState((payment.refundableCents / 100).toFixed(2));
  const [reason, setReason] = useState<'' | RefundReason>('requested_by_customer');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // One key per REQUEST, not per opening: a retry of the same refund (a lost
  // answer) replays safely and moves no money twice, while a changed amount,
  // reason or note is a new request Stripe must not refuse as a reused key.
  const key = useMemo(newKey, [mode, amount, reason, note]);

  const cents = mode === 'full' ? payment.refundableCents : parseAmountCents(amount);
  const over = cents !== null && cents > payment.refundableCents;
  const invalid = cents === null || over;
  const cap = money(payment.refundableCents, payment.currency);
  const whom = who(payment);

  async function submit() {
    if (invalid || busy) return;
    setBusy(true); setError('');
    try {
      const r = await refundPayment({
        sessionId: payment.id,
        ...(mode === 'partial' ? { amountCents: cents! } : {}),
        reason: reason || null,
        ...(note.trim() ? { note: note.trim() } : {}),
        idempotencyKey: key,
      });
      onDone(r.payment, r.refund.amountCents);
    } catch (e) {
      setError((e as Error).message || 'Stripe could not make the refund.');
      setBusy(false);
    }
  }

  return (
    <Modal
      // The question names the amount the button will refund.
      title={`Refund ${cents !== null && !over ? money(cents, payment.currency) : cap} to ${whom}?`}
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <button className="fs-btn danger solid" disabled={invalid || busy} onClick={() => void submit()}>
            {busy ? 'Refunding…' : `Refund ${cents !== null && !over ? money(cents, payment.currency) : ''}`.trim()}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="bp-refund">
        <div className="bp-refund-what">
          {payment.formName || 'A form'}{payment.submissionId ? ` · ${payment.submissionId}` : ''}
          {' · paid '}{money(payment.amountCents, payment.currency)} {whenWords(payment.createdAt).toLowerCase()}
          {payment.refundedCents > 0 && (
            <div className="st-muted">{money(payment.refundedCents, payment.currency)} already refunded — {cap} left.</div>
          )}
        </div>

        <div className="fs-seg" role="group" aria-label="How much">
          <button type="button" className={mode === 'full' ? 'on' : ''} aria-pressed={mode === 'full'} onClick={() => setMode('full')}>
            Full refund · {cap}
          </button>
          <button type="button" className={mode === 'partial' ? 'on' : ''} aria-pressed={mode === 'partial'} onClick={() => setMode('partial')}>
            Partial
          </button>
        </div>

        {mode === 'partial' && (
          <div className="fs-field">
            <label htmlFor="bp-refund-amount">Amount ({payment.currency.toUpperCase()})</label>
            <input id="bp-refund-amount" inputMode="decimal" autoComplete="off" value={amount}
              onChange={(e) => setAmount(e.target.value)} aria-invalid={invalid}
              onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
            <div className={`hint${invalid ? ' bp-hint-bad' : ''}`}>
              {over ? `At most ${cap} — that is what is left to refund.`
                : cents === null ? 'Enter an amount, like 25 or 25.50.'
                  : `Up to ${cap}.`}
            </div>
          </div>
        )}

        <div className="fs-field">
          <label htmlFor="bp-refund-reason">Reason</label>
          <select id="bp-refund-reason" value={reason} onChange={(e) => setReason(e.target.value as '' | RefundReason)}>
            {REASONS.map((r) => <option key={r.value || 'other'} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        <div className="fs-field">
          <label htmlFor="bp-refund-note">Note (optional)</label>
          <textarea id="bp-refund-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Kept on the refund in Stripe and in your audit log" />
        </div>

        <div className="fs-consequence warn">
          {cents !== null && !over ? money(cents, payment.currency) : 'The amount'} goes back to {whom}&apos;s card
          from your Stripe balance. It can&apos;t be undone from here.
        </div>
        {error && <div className="ad-err">{error}</div>}
      </div>
    </Modal>
  );
}
